import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Dumbbell, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import { announceBadges } from "../components/announce-badges";
import { FormShell, WhenField } from "../components/form-shell";

// Each protocol seeds new exercises with a sets×reps template. GVT (10×10) is the
// default; switching protocols only changes the template for exercises added next.
const PROTOCOLS: Record<string, { label: string; sets: number; reps: number }> = {
	gvt: { label: "German Volume Training (10×10)", sets: 10, reps: 10 },
	"5x5": { label: "5×5", sets: 5, reps: 5 },
	"3x8": { label: "3×8 hypertrophy", sets: 3, reps: 8 },
	custom: { label: "Custom", sets: 3, reps: 8 },
};

type ExerciseRow = {
	id: string;
	name: string;
	setsCount: number;
	reps: number;
	weightKg: number;
	rpe: string;
};

const DEFAULT_TEMPLATE = { label: "Custom", sets: 3, reps: 8 };

function templateExercise(protocol: string): ExerciseRow {
	const t = PROTOCOLS[protocol] ?? DEFAULT_TEMPLATE;
	return {
		id: crypto.randomUUID(),
		name: "",
		setsCount: t.sets,
		reps: t.reps,
		weightKg: 0,
		rpe: "",
	};
}

export function LogStrengthPage() {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [protocol, setProtocol] = useState("gvt");
	const [when, setWhen] = useState("");
	const [notes, setNotes] = useState("");
	const [durationMin, setDurationMin] = useState("");
	const [exercises, setExercises] = useState<ExerciseRow[]>([templateExercise("gvt")]);

	const update = (i: number, patch: Partial<ExerciseRow>) =>
		setExercises((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

	const save = useMutation({
		mutationFn: () => {
			const payload = {
				protocol,
				startTime: when || undefined,
				notes: notes || undefined,
				durationSec: durationMin ? Math.round(Number(durationMin) * 60) : undefined,
				exercises: exercises
					.filter((e) => e.name.trim())
					.map((e) => ({
						name: e.name.trim(),
						sets: Array.from({ length: Math.max(1, e.setsCount) }, () => ({
							reps: e.reps,
							weightKg: e.weightKg,
							...(e.rpe ? { rpe: Number(e.rpe) } : {}),
						})),
					})),
			};
			return fitstreakApi.logStrength({ input: payload });
		},
		onSuccess: (res) => {
			toast.success("Strength session logged");
			announceBadges(res.newBadges);
			qc.invalidateQueries({ queryKey: ["fitstreak"] });
			navigate({ to: "/fitstreak" });
		},
		meta: { errorMessage: "Failed to log session" },
	});

	const canSave = exercises.some((e) => e.name.trim()) && !save.isPending;

	return (
		<FormShell
			icon={Dumbbell}
			title="Log strength"
			subtitle="German Volume Training is the default — change the protocol to seed a different template."
			actions={
				<>
					<Button variant="ghost" size="sm" onClick={() => navigate({ to: "/fitstreak" })}>
						Cancel
					</Button>
					<Button size="sm" disabled={!canSave} onClick={() => save.mutate()}>
						Log session
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-1.5">
				<Label htmlFor="protocol">Protocol</Label>
				<Select value={protocol} onValueChange={setProtocol}>
					<SelectTrigger id="protocol" className="w-full">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{Object.entries(PROTOCOLS).map(([key, p]) => (
							<SelectItem key={key} value={key}>
								{p.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col gap-3">
				<Label>Exercises</Label>
				{exercises.map((ex, i) => (
					<div
						key={ex.id}
						className="flex flex-col gap-2 rounded-lg border border-hairline bg-surface-1 p-3"
					>
						<div className="flex items-center gap-2">
							<Input
								placeholder="Exercise (e.g. Back Squat)"
								value={ex.name}
								onChange={(e) => update(i, { name: e.target.value })}
							/>
							{exercises.length > 1 ? (
								<button
									type="button"
									aria-label="Remove exercise"
									className="flex size-8 flex-none items-center justify-center rounded-md text-ink-subtle hover:bg-destructive/10 hover:text-destructive"
									onClick={() => setExercises((rows) => rows.filter((_, idx) => idx !== i))}
								>
									<Trash2 className="size-4" />
								</button>
							) : null}
						</div>
						<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
							<NumField
								label="Sets"
								value={ex.setsCount}
								min={1}
								onChange={(v) => update(i, { setsCount: v })}
							/>
							<NumField
								label="Reps"
								value={ex.reps}
								min={1}
								onChange={(v) => update(i, { reps: v })}
							/>
							<NumField
								label="Weight (kg)"
								value={ex.weightKg}
								min={0}
								step={0.5}
								onChange={(v) => update(i, { weightKg: v })}
							/>
							<div className="flex flex-col gap-1">
								<span className="text-meta text-ink-subtle">RPE (opt)</span>
								<Input
									type="number"
									min={1}
									max={10}
									value={ex.rpe}
									onChange={(e) => update(i, { rpe: e.target.value })}
								/>
							</div>
						</div>
					</div>
				))}
				<Button
					variant="outline"
					size="sm"
					className="self-start"
					onClick={() => setExercises((rows) => [...rows, templateExercise(protocol)])}
				>
					<Plus className="size-4" /> Add exercise
				</Button>
			</div>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<WhenField value={when} onChange={setWhen} />
				<div className="flex flex-col gap-1.5">
					<Label htmlFor="duration">Duration (min, optional)</Label>
					<Input
						id="duration"
						type="number"
						min={1}
						value={durationMin}
						onChange={(e) => setDurationMin(e.target.value)}
					/>
				</div>
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor="notes">Notes (optional)</Label>
				<Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
			</div>
		</FormShell>
	);
}

function NumField({
	label,
	value,
	min,
	step,
	onChange,
}: {
	label: string;
	value: number;
	min?: number;
	step?: number;
	onChange: (v: number) => void;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-meta text-ink-subtle">{label}</span>
			<Input
				type="number"
				min={min}
				step={step}
				value={value}
				onChange={(e) => onChange(Number(e.target.value))}
			/>
		</div>
	);
}
